import {
  GRID_LINE_COUNT,
  GRID_LINE_COLOR,
  ZONE_LABEL_COLOR
} from '../../consts'
import {MonthlyHierarchyAnalysisDataPoint, YearlyHierarchyAnalysisDataPoint} from '../../types'
import {getTotal} from './difficultyScore.utils'

type DifficultyBoundaries = {
  basicMax: number
  advancedMax: number
  totalQuestions: number
}

type ZoneLabelParams = {
  boundaries?: DifficultyBoundaries
  maxQuestionCount: number
  basicLabel: string
  advancedLabel: string
  excellenceLabel: string
}

export const computeBoundariesFromData = (
  data: MonthlyHierarchyAnalysisDataPoint[] | YearlyHierarchyAnalysisDataPoint[]
): DifficultyBoundaries | undefined => {
  const withScores = data.find(
    (dp) => dp.difficultyScores?.basic && dp.difficultyScores?.advanced && dp.totalScore
  )
  if (!withScores?.difficultyScores || !withScores.totalScore) return undefined

  const basicMax = getTotal(withScores.difficultyScores.basic)
  const advancedMax = basicMax + getTotal(withScores.difficultyScores.advanced)
  const totalQuestions = withScores.totalScore.questionCount

  return {basicMax, advancedMax, totalQuestions}
}

export const buildMarkLineConfig = (
  _boundaries: DifficultyBoundaries | undefined,
  maxQuestionCount: number
) => {
  const gridLines = Array.from({length: GRID_LINE_COUNT - 1}, (_, i) => ({
    yAxis: Math.round((maxQuestionCount * (i + 1)) / GRID_LINE_COUNT),
    lineStyle: {type: 'solid' as const, color: GRID_LINE_COLOR, width: 1}
  }))

  // Orange (basic/advanced) and green (advanced/excellence) boundary lines removed per design
  return {
    markLine: {
      silent: true,
      symbol: 'none',
      label: {show: false},
      data: gridLines
    }
  }
}

export const buildZoneLabelAxis = ({
  boundaries,
  maxQuestionCount,
  basicLabel,
  advancedLabel,
  excellenceLabel
}: ZoneLabelParams) => {
  if (!boundaries) {
    return {type: 'value' as const, show: false}
  }

  const basicMid = Math.round(boundaries.basicMax / 2)
  const advancedMid = Math.round((boundaries.basicMax + boundaries.advancedMax) / 2)
  const excellenceMid = Math.round((boundaries.advancedMax + maxQuestionCount) / 2)

  return {
    type: 'value' as const,
    position: 'left' as const,
    offset: 40,
    min: 0,
    max: maxQuestionCount,
    interval: 1,
    axisLine: {show: false},
    axisTick: {show: false},
    splitLine: {show: false},
    axisLabel: {
      show: true,
      rotate: 90,
      fontSize: 11,
      color: ZONE_LABEL_COLOR,
      fontStyle: 'italic' as const,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        if (value === basicMid) return basicLabel
        if (value === advancedMid) return advancedLabel
        if (value === excellenceMid) return excellenceLabel
        return ''
      }
    }
  }
}
